import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { ENDPOINTS } from "@/const/endpoints";
import { resolveTelegramInitData } from "@/utils/telegramWebApp";

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const fetchClient = useFetch();
  const [user, setUser] = useState(null);
  const [pendingTwoFactor, setPendingTwoFactor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const login = useCallback(() => {
    const accessToken = window.localStorage.getItem("accessToken");
    if (!accessToken) {
      setUser(null);
      setLoading(false);
      return Promise.resolve(null);
    }

    setLoading(true);
    setError(null);

    return fetchClient
      .get(ENDPOINTS.USER_ME)
      .then((res) => {
        setUser(res.data);
        setPendingTwoFactor(null);
        return res.data;
      })
      .catch((e) => {
        window.localStorage.removeItem("accessToken");
        window.localStorage.removeItem("refreshToken");
        setUser(null);
        setError(e?.message ?? String(e ?? ""));
        return null;
      })
      .finally(() => setLoading(false));
  }, [fetchClient]);

  // Обновление данных пользователя без loading state (для polling).
  // Не зависит от `user`: иначе после каждого ответа /me меняется ссылка на callback,
  // эффекты с [refreshUser] пересоздают интервал и сразу вызывают tick() — лавина запросов.
  const refreshUser = useCallback(() => {
    const accessToken = window.localStorage.getItem("accessToken");
    if (!accessToken) {
      return Promise.resolve(null);
    }

    return fetchClient
      .get(ENDPOINTS.USER_ME, { skipNetworkErrorToast: true })
      .then((res) => {
        setUser(res.data);
        return res.data;
      })
      .catch((e) => {
        // Тихо игнорируем ошибки при обновлении
        console.error("Failed to refresh user data:", e);
        return null;
      });
  }, [fetchClient]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlAccess = params.get("accessToken");
    const urlRefresh = params.get("refreshToken");
    if (urlAccess) {
      window.localStorage.setItem("accessToken", urlAccess);
      if (urlRefresh) {
        window.localStorage.setItem("refreshToken", urlRefresh);
      }
      params.delete("accessToken");
      params.delete("refreshToken");
      const rest = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${rest ? `?${rest}` : ""}${window.location.hash}`,
      );
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resolveSession() {
      // Wait for telegram-web-app.js + initData before deciding this is not a Mini App session.
      // Otherwise resolveSession races the async SDK load and skips POST /api/auth/telegram.
      const rawInit = await resolveTelegramInitData();
      if (cancelled) return;

      const tg = typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;

      if (rawInit) {
        setLoading(true);
        setError(null);
        try {
          // Реферальный код и промокод приходят либо прямо в start_param Mini App-ссылки
          // (см. App.js — сохраняет их в localStorage ДО того, как initData становится непустой),
          // либо уже лежат в localStorage от предыдущей попытки — читаем оба источника.
          const startParam = tg?.initDataUnsafe?.start_param;
          const storedReferralCode = window.localStorage.getItem("referralCode");
          const storedPromoCode = window.localStorage.getItem("promoCode");
          const ref =
            (startParam && startParam.startsWith("ref_") ? startParam.substring(4) : null) ||
            (storedReferralCode && storedReferralCode.startsWith("ref_")
              ? storedReferralCode.substring(4)
              : null);
          const promoCode =
            (startParam && startParam.startsWith("promo_") ? startParam.substring(6) : null) ||
            storedPromoCode ||
            null;

          // Mini App identity wins: do not send a leftover website JWT (expired or another account).
          const res = await fetchClient.post(
            ENDPOINTS.AUTH_TELEGRAM,
            { ref, promoCode },
            { skipAuthHeader: true },
          );
          const d = res.data || {};
          if (d.twoFactorRequired && d.challengeToken) {
            window.localStorage.removeItem("accessToken");
            window.localStorage.removeItem("refreshToken");
            setPendingTwoFactor(d.challengeToken);
          } else {
            if (d.accessToken) window.localStorage.setItem("accessToken", d.accessToken);
            if (d.refreshToken) window.localStorage.setItem("refreshToken", d.refreshToken);
            if (d.accessToken) {
              if (storedReferralCode) window.localStorage.removeItem("referralCode");
              if (storedPromoCode) window.localStorage.removeItem("promoCode");
            }
          }
        } catch (e) {
          console.warn("[UserProvider] Telegram Mini App auth failed:", e?.message ?? e);
        }
      }

      if (cancelled) return;
      await login();
    }

    resolveSession();
    return () => {
      cancelled = true;
    };
  }, [fetchClient, login]);

  const logout = useCallback(() => {
    window.localStorage.removeItem("accessToken");
    window.localStorage.removeItem("refreshToken");
    setUser(null);
    setPendingTwoFactor(null);
    setLoading(false);
    setError(null);
  }, []);

  const beginTwoFactor = useCallback((token) => {
    setPendingTwoFactor(token);
  }, []);

  const clearTwoFactor = useCallback(() => {
    setPendingTwoFactor(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      pendingTwoFactor,
      logout,
      login,
      refreshUser,
      beginTwoFactor,
      clearTwoFactor,
      setUser,
      token: window.localStorage.getItem("accessToken") ?? null,
    }),
    [user, loading, error, pendingTwoFactor, logout, login, refreshUser, beginTwoFactor, clearTwoFactor],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  return useContext(UserContext);
}
